import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';

import { alertError } from '../../store/actions/alert';
import { exportProducts } from '../../store/actions/products';
import { getInventory, getProductFilters } from '../../store/actions/api/suppliers';
import { getTotalCountHeader } from '../../store/actions/api/utils';
import {
  getInventoryHistoryLog,
  setInventoryHistoryVariant,
  setInventoryHistoryOffset,
  inventoryHistoryToggleModal,
  setInventoryLogsLoading,
} from '../../store/actions/historyLog';
import { setInventory } from '../../store/actions/supplierInventory';
import { setProductFilters } from '../../store/actions/productFilters';

import ActionButton from '../../components/common/ActionButton';
import InventoryHistoryLogModal from '../../components/modals/InventoryHistoryLogModal';
import SupplierInventory from '../../components/blocks/SupplierInventory';
import Toolbar from '../../components/common/Toolbar';

import BulkEditInventoryBuffersModal from '../../components/modals/BulkEditInventoryBuffersModal/BulkEditInventoryBuffersModal';
import BulkInventoryEditActionsDropdown from '../../components/common/BulkInventoryEditActionsDropdown/BulkInventoryEditActionsDropdown';
import { useBufferByVariant } from '../../hooks/useBufferByVariantPermission';
import { useIsSupplierAdmin } from '../../hooks/useIsSupplierAdmin';
import styles from '../../components/blocks/SupplierInventory/SupplierInventory.module.scss';
import BulkEditInventoryQuantitiesModal from '../../components/modals/BulkEditInventoryQuantitiesModal';
import { isFeatureFlagEnabledForUserOrSupplier } from '../../utils/user-based-feature-flag';
import { useInventoryManualUpdateByPopupAndFile } from '../../hooks/useInventoryManualUpdatePermission';
import { UploadInventoryFileModal } from '../../components/modals/UploadInventoryFileModal/UploadInventoryFileModal';
import { UPLOAD_INVENTORY_FILE_MODAL_TYPES } from '../../constants/uploadInventoryFileModalTypes';
import { useEditVariantBufferByFileUpload } from '../../hooks/useEditVariantBufferByFileUpload';

function SupplierInventoryContainer({
  supplierId,
  numberOfProductsPerPage,
  offset,
  searchText,
  currentFilters,
  productsOrder,
  productsOrderField,
  alertError,
  setInventory,
  setProductFilters,
  historyLog,
  historyLogToggleModal,
  setInventoryLogsLoading,
  historyLogSetVariant,
  historyLogSetPage,
  getInventoryHistoryLog,
  exportProducts,
}) {
  /*
   * Lots of feature flags here, but we will soon deprecate them once the features are ready
   * Jira to remove: https://jira.vtex.com/browse/VTEX-13170
   */
  const editBufferFeatureFlagConfig = useBufferByVariant();
  const isUserAllowedToEditInventoryByPopupAndFile = useInventoryManualUpdateByPopupAndFile();
  const editBufferByFileFeatureFlagConfig = useEditVariantBufferByFileUpload();

  const isSupplierAdmin = useIsSupplierAdmin();

  const {
    selectedVariants,
    loading: { export: loadingExport },
  } = useSelector((state) => state.supplierInventory);

  const warehousesEligibleForManualUpdate = useSelector((state) => {
    const { warehouses = [] } = state?.role?.supplier || {};
    return warehouses.filter((warehouse) => warehouse.is_enabled && !warehouse.is_full_update);
  });

  const [isBufferBulkEditModalOpen, setBufferBulkEditModelOpen] = useState(false);
  const [isInventoryQuantityBulkEditModalOpen, setInventoryQuantityBulkEditModalOpen] = useState(false);
  const [uploadInventoryModalConfig, setUploadInventoryModalConfig] = useState({
    isOpen: false,
    modalType: null,
  });

  const openHistoryLogModal = (variant) => {
    historyLogToggleModal(true);
    setInventoryLogsLoading(true);
    historyLogSetVariant(variant);
    getInventoryHistoryLog();
  };

  const baseFields = ['id', 'sku', 'model', 'upc', 'ean', 'title', 'available_inventory', 'last_inventory_update'];

  const requestedFields = isFeatureFlagEnabledForUserOrSupplier(editBufferFeatureFlagConfig)
    ? baseFields.concat('variant_inventory_buffer')
    : baseFields;

  const fetchInventory = useCallback(async () => {
    try {
      const response = await getInventory(
        supplierId,
        currentFilters,
        offset,
        numberOfProductsPerPage,
        searchText,
        productsOrder,
        productsOrderField,
        {},
        requestedFields,
      );

      setInventory({ data: response.data, count: getTotalCountHeader(response) });
    } catch (error) {
      alertError(error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId, currentFilters, offset, numberOfProductsPerPage, searchText, productsOrder, productsOrderField]);

  React.useEffect(
    function initializeFetchInventory() {
      fetchInventory();
    },
    [fetchInventory],
  );

  React.useEffect(function initializeFilters() {
    (async () => {
      try {
        const response = await getProductFilters(supplierId);

        setProductFilters(response.data);
      } catch (error) {
        alertError(error);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const toggleBufferBulkBulkEditModal = () => {
    setBufferBulkEditModelOpen((prevState) => !prevState);
  };

  const bulkEditModalActions = [
    {
      id: 'buffer',
      label: !!selectedVariants.ids.length ? `Edit Buffers (${selectedVariants.ids.length})` : 'Edit Buffers',
      isButtonEnabled: !!selectedVariants.ids.length,
      onClick: () => setBufferBulkEditModelOpen(true),
      isFeatureEnabled: isFeatureFlagEnabledForUserOrSupplier(editBufferFeatureFlagConfig),
    },
    {
      id: 'quantity',
      label: !!selectedVariants.ids.length ? `Edit Inventory (${selectedVariants.ids.length})` : 'Edit Inventory',
      isButtonEnabled: !!selectedVariants.ids.length,
      onClick: () => setInventoryQuantityBulkEditModalOpen(true),
      isFeatureEnabled:
        isSupplierAdmin && warehousesEligibleForManualUpdate.length && isUserAllowedToEditInventoryByPopupAndFile,
    },
  ];

  const bulkEditFileActions = [
    {
      id: 'update-buffer-by-file',
      label: 'Upload buffer file',
      onClick: () =>
        setUploadInventoryModalConfig({ isOpen: true, modalType: UPLOAD_INVENTORY_FILE_MODAL_TYPES.buffers }),
      isButtonEnabled: true,
      uiOptions: { shouldHaveAdditionalPaddingTop: true },
      isFeatureEnabled: isSupplierAdmin && isFeatureFlagEnabledForUserOrSupplier(editBufferByFileFeatureFlagConfig),
    },
    {
      id: 'manual-update-by-file',
      label: 'Upload inventory file',
      onClick: () =>
        setUploadInventoryModalConfig({ isOpen: true, modalType: UPLOAD_INVENTORY_FILE_MODAL_TYPES.quantity }),
      isButtonEnabled: true,
      uiOptions: { shouldHaveAdditionalPaddingTop: true },
      isFeatureEnabled:
        isSupplierAdmin && warehousesEligibleForManualUpdate.length && isUserAllowedToEditInventoryByPopupAndFile,
    },
  ];

  const dropdownActions = [...bulkEditModalActions, ...bulkEditFileActions];

  const shouldRenderBulkEditInventoryActionsDropdown = dropdownActions.some((action) => action.isFeatureEnabled);

  return (
    <div>
      <Toolbar name="Inventory">
        <div className={styles.toolbar}>
          {shouldRenderBulkEditInventoryActionsDropdown && (
            <BulkInventoryEditActionsDropdown
              actions={dropdownActions}
              fontSize="14px"
              isDisabled={!selectedVariants.ids?.length}
            />
          )}
          <ActionButton
            type="export"
            title="Export"
            onClick={exportProducts}
            size={22}
            shouldUseModernStyles
            downloading={loadingExport}
            tooltipContent="Export Inventory"
          />
        </div>
      </Toolbar>

      <SupplierInventory onOpenHistoryLog={openHistoryLogModal} />
      <BulkEditInventoryBuffersModal open={isBufferBulkEditModalOpen} onClose={toggleBufferBulkBulkEditModal} />
      <BulkEditInventoryQuantitiesModal
        isOpen={isInventoryQuantityBulkEditModalOpen}
        onClose={() => {
          setInventoryQuantityBulkEditModalOpen(false);
          setBufferBulkEditModelOpen(false);
        }}
        warehouses={warehousesEligibleForManualUpdate}
        variantIds={selectedVariants.ids}
        variantsWithDetails={selectedVariants.variantsWithDetails}
        fetchInventory={fetchInventory}
      />

      {historyLog.open && (
        <InventoryHistoryLogModal
          {...historyLog}
          onClose={() => historyLogToggleModal(false)}
          setPage={historyLogSetPage}
        />
      )}
      <UploadInventoryFileModal
        isOpen={uploadInventoryModalConfig.isOpen}
        modalType={uploadInventoryModalConfig.modalType}
        warehouses={warehousesEligibleForManualUpdate}
        onClose={() => setUploadInventoryModalConfig(false)}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  supplierId: state.role.supplierId,
  numberOfProductsPerPage: state.supplierInventory.perPage,
  offset: state.supplierInventory.offset,
  searchText: state.supplierInventory.searchText,
  loading: state.supplierInventory.loading,
  currentFilters: state.productFilters.currentFilters,
  productsOrder: state.productFilters.order,
  productsOrderField: state.productFilters.orderField,
  historyLog: state.historyLog,
});

export default connect(mapStateToProps, {
  setInventory,
  alertError,
  setProductFilters,
  exportProducts,
  historyLogToggleModal: inventoryHistoryToggleModal,
  historyLogSetPage: setInventoryHistoryOffset,
  setInventoryLogsLoading,
  historyLogSetVariant: setInventoryHistoryVariant,
  getInventoryHistoryLog,
})(SupplierInventoryContainer);

SupplierInventoryContainer.propTypes = {
  historyLogSetPage: PropTypes.func.isRequired,
};
